import { FC, ReactNode } from 'react';
import type { RequirementGroup } from '@teachur/http-client';
import { cn } from '@new/lib';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';
import { isCourseCompleted, sumCredits } from '../../lib/planView';

interface DegreePlanProgressBarProps {
  groups: RequirementGroup[];
  onSelectGroup?: (groupId: string) => void;
  legendExtra?: ReactNode;
  showHint?: boolean;
}

const COMPLETED_COLOR = 'bg-(--success-S600)';
const TO_TAKE_COLOR = 'bg-(--primary-O900)';
const EMPTY_COLOR = 'bg-(--neutral-black-NB200)';

interface GroupFill {
  credits: number;
  color: string;
}

interface GroupSection {
  groupId: string;
  requiredCredits: number;
  completed: boolean;
  title: string;
  fills: GroupFill[];
}

const groupSection = (group: RequirementGroup): GroupSection | null => {
  if (group.requiredCredits <= 0) {
    return null;
  }
  const completedCredits = sumCredits(group.courses.filter(isCourseCompleted));
  const toTakeCredits = sumCredits(group.courses.filter((c) => !isCourseCompleted(c)));
  const emptyCredits = Math.max(group.requiredCredits - completedCredits - toTakeCredits, 0);

  const parts = [`${completedCredits}/${group.requiredCredits} credits completed`];
  if (toTakeCredits > 0) {
    parts.push(`${toTakeCredits}/${group.requiredCredits} to take on Newlane`);
  }
  if (emptyCredits > 0) {
    parts.push(`${emptyCredits}/${group.requiredCredits} left to choose`);
  }

  const fills: GroupFill[] = [
    { credits: completedCredits, color: COMPLETED_COLOR },
    { credits: toTakeCredits, color: TO_TAKE_COLOR },
    { credits: emptyCredits, color: EMPTY_COLOR },
  ].filter((fill) => fill.credits > 0);

  return {
    groupId: group.id,
    requiredCredits: group.requiredCredits,
    completed: emptyCredits === 0 && toTakeCredits === 0,
    title: `${group.title}: ${parts.join(', ')}`,
    fills,
  };
};

export const DegreePlanProgressBar: FC<DegreePlanProgressBarProps> = ({
  groups,
  onSelectGroup,
  legendExtra,
  showHint = true,
}) => {
  const creditsRequired = groups.reduce((total, group) => total + group.requiredCredits, 0);
  const sections = groups
    .map(groupSection)
    .filter((section): section is GroupSection => section != null);

  if (creditsRequired <= 0 || sections.length === 0) {
    return null;
  }

  const onSectionClick = (groupId: string) => () => onSelectGroup?.(groupId);

  return (
    <div className="flex w-full flex-col gap-2">
      <div
        role="group"
        aria-label={sections.map((section) => section.title).join('; ')}
        className="flex h-5.25 w-full gap-0.5 rounded-xl! border border-(--neutral-black-NB300) p-0.75"
      >
        {sections.map((section, index) => {
          const clickable = onSelectGroup != null;
          const isFirst = index === 0;
          const isLast = index === sections.length - 1;
          const borderRadius =
            isFirst && isLast
              ? '10px'
              : isFirst
                ? '10px 0 0 10px'
                : isLast
                  ? '0 10px 10px 0'
                  : undefined;
          return (
            <Tooltip
              key={section.groupId}
              content={
                <span
                  className={
                    section.completed ? 'text-(--success-S700)' : 'text-(--neutral-black-NB900)'
                  }
                >
                  {section.title}
                </span>
              }
              onClick={clickable ? onSectionClick(section.groupId) : undefined}
              style={{
                width: `${(section.requiredCredits / creditsRequired) * 100}%`,
                borderRadius,
              }}
              wrapperClassName="relative flex h-full min-w-px appearance-none overflow-hidden border-0 p-0 transition-transform duration-150 cursor-pointer hover:z-20 hover:scale-y-125"
            >
              {section.fills.map((fill, fillIndex) => {
                const isFirstFill = isFirst && fillIndex === 0;
                const isLastFill = isLast && fillIndex === section.fills.length - 1;
                const fillRadius =
                  isFirstFill && isLastFill
                    ? '10px'
                    : isFirstFill
                      ? '10px 0 0 10px'
                      : isLastFill
                        ? '0 10px 10px 0'
                        : undefined;
                return (
                  <span
                    key={fillIndex}
                    className={cn('block h-full', fill.color)}
                    style={{
                      width: `${(fill.credits / section.requiredCredits) * 100}%`,
                      borderRadius: fillRadius,
                    }}
                  />
                );
              })}
              <span className="sr-only">{section.title}</span>
            </Tooltip>
          );
        })}
      </div>

      <div className="flex items-center gap-5">
        {[
          { label: 'Completed', color: COMPLETED_COLOR },
          { label: 'Take on Newlane', color: TO_TAKE_COLOR },
        ].map((item) => (
          <span key={item.label} className="flex items-center gap-1.5">
            <span className={cn('size-1.5 rounded-sm!', item.color)} />
            <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
              {item.label}
            </span>
          </span>
        ))}
        {showHint && (
          <span className="ml-auto [font-size:var(--fs-l5)]! italic text-(--neutral-black-NB500)!">
            Hover over a section to see its category
          </span>
        )}
        {legendExtra && <span className="ml-auto">{legendExtra}</span>}
      </div>
    </div>
  );
};
